import React from 'react';
import PropTypes from 'prop-types';
import { Anchor, Container } from 'components/base';

import PdpIcon from '../product/pdpIcon';
import { GIFT_CARD_HREF } from './shared';
import { Picture, Responsive, SectionHead, imageShape, landingImage, productImage } from '../landing/shared';

// Tiles linking the gift pages to each other. Titles keep the anchor text of the links the
// pages had before the redesign. Budget amounts are category thresholds and never change.
export const BUDGET_TILES = {
  under20: {
    href: '/gifts-under-20',
    title: 'Shop Gifts Under $20',
    pre: 'Under',
    amount: '$20',
    tone: 'terracotta',
    description: 'Square Prints, Softcover Photo Books, the Wood Photo Block and more.',
    short: 'Square Prints, books, Wood Photo Block',
    cta: 'See the gifts',
    image: productImage('wood-block/1'),
  },
  under50: {
    href: '/gifts-under-50',
    title: 'Shop Gifts Under $50',
    pre: 'Under',
    amount: '$50',
    tone: 'ochre',
    description: 'Hardcover Photo Books, the Keepsake Wall Calendar, Wood Poster Rails and more.',
    short: 'Hardcover books, calendars, rails',
    cta: 'See the gifts',
    image: landingImage('hc-couch-open'),
  },
  giftCards: {
    href: GIFT_CARD_HREF,
    title: 'Gift Cards',
    pre: 'Any amount',
    prePhone: 'From',
    amount: '$10–100',
    amountPhone: '$10',
    tone: 'green',
    description: 'Emailed in minutes, or printed at home. They choose the photos.',
    short: '$10 to $100, emailed in minutes.',
    cta: 'Send a Gift Card',
    image: productImage('classic-prints/2'),
    giftCard: true,
  },
  // "From $10" is static (Square Prints), like the /gifts meta description.
  gifts: {
    href: '/gifts',
    title: 'Personalized photo gifts',
    pre: 'From',
    amount: '$10',
    tone: 'cream',
    description: 'Gift ideas by person, each a print paired with a place to keep it.',
    short: 'Gift ideas by person',
    cta: 'See gift ideas',
    image: productImage('classic-prints/4'),
  },
};

const BudgetTiles = ({ title, tiles, couponClaimed, track }) => (
  <section className="lnd-section gft-budget">
    <Container>
      <SectionHead eyebrow="Shop by budget" title={title} className="lnd-head--center" />
      <ul className="gft-budget-list">
        {tiles.map((tile) => (
          <li key={tile.href} className={`gft-budget-tile gft-budget-tile--${tile.tone}`}>
            <p className="gft-budget-price">
              <span className="gft-budget-pre">
                <Responsive desktop={tile.pre} phone={tile.prePhone} />
              </span>
              <span className="gft-budget-amount">
                <Responsive desktop={tile.amount} phone={tile.amountPhone} />
              </span>
            </p>
            <div className="gft-budget-photo">
              <Picture image={tile.image} sizes="320px" />
            </div>
            <div className="gft-budget-copy">
              <h3 className="gft-budget-title">
                <Anchor
                  href={tile.href}
                  className="gft-budget-link"
                  onClick={track('budget', 'budget', tile.href)}
                >
                  {tile.title}
                </Anchor>
              </h3>
              <p className="gft-budget-desc">
                <Responsive desktop={tile.description} phone={tile.short} />
                {tile.giftCard && couponClaimed && (
                  <span className="gft-budget-note"> Coupons don’t apply to Gift Cards.</span>
                )}
              </p>
            </div>
            <span className="lnd-arrow-link gft-budget-cta" aria-hidden="true">
              {tile.cta}
              <PdpIcon name="arrowRight" size={14} />
            </span>
            <span className="gft-go" aria-hidden="true">
              <PdpIcon name="arrowRight" size={16} />
            </span>
          </li>
        ))}
      </ul>
    </Container>
  </section>
);

BudgetTiles.propTypes = {
  title: PropTypes.string.isRequired,
  tiles: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      pre: PropTypes.string.isRequired,
      prePhone: PropTypes.string,
      amount: PropTypes.string.isRequired,
      amountPhone: PropTypes.string,
      tone: PropTypes.oneOf(['terracotta', 'ochre', 'green', 'cream']).isRequired,
      description: PropTypes.string.isRequired,
      short: PropTypes.string,
      cta: PropTypes.string.isRequired,
      image: imageShape.isRequired,
      giftCard: PropTypes.bool,
    }),
  ).isRequired,
  // Any claimed coupon but shipping-only, the Gift Card tile then says coupons don't apply.
  couponClaimed: PropTypes.bool,
  track: PropTypes.func.isRequired,
};

export default BudgetTiles;
